import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { X } from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import { countryCodes } from '../utils/countryCodes';

const InquiryModal = ({ isOpen, onClose, buttonContext = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: ''
  });

  const [countryCode, setCountryCode] = useState('+1');
  const [detectedCountry, setDetectedCountry] = useState('US');
  const [geoData, setGeoData] = useState(null);

  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch geolocation when modal opens
  React.useEffect(() => {
    if (isOpen) {
      const fetchGeoLocation = async () => {
        try {
          const { getGeoLocation, getCountryCodeFromGeo } = await import('../services/leadApi');
          const geoLocationData = await getGeoLocation();
          
          if (geoLocationData?.isSuccess) {
            setGeoData(geoLocationData);
            const countryInfo = getCountryCodeFromGeo(geoLocationData);
            setCountryCode(countryInfo.countryCode);
            setDetectedCountry(countryInfo.country);
          }
        } catch (error) {
          console.error('Failed to fetch geolocation:', error);
        }
      };
      
      fetchGeoLocation();
    }
  }, [isOpen]);

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation - exactly 10 digits
  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/[^\d]/g, '');
    return digitsOnly.length === 10;
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' }));
    } else {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePhoneBlur = () => {
    if (formData.phone && !validatePhone(formData.phone)) {
      setErrors(prev => ({ ...prev, phone: 'Must be exactly 10 digits' }));
    } else {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check terms agreement
    if (!agreedToTerms) {
      toast.error('Please agree to the terms before submitting');
      return;
    }

    // Validate before submission
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
      hasErrors = true;
    }

    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Must be exactly 10 digits';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsLoading(true);

    try {
      // Import lead API dynamically
      const { submitLead } = await import('../services/leadApi');
      
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        countryCode: countryCode,
        country: detectedCountry,
        description: `${buttonContext ? `Context: ${buttonContext}\n\n` : ''}${formData.description}`
      };

      // Use pre-fetched geoData
      const result = await submitLead(leadData, geoData);
      
      if (result.success) {
        toast.success(result.message || 'Your inquiry has been submitted successfully!');
        // Redirect will be handled automatically by submitLead
      } else {
        toast.error(result.message || 'Failed to submit. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again later.');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-300">
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Left Side - Image */}
          <div 
            className="hidden md:block md:w-2/5 bg-cover bg-center relative"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1556761175-b413da4baf72?q=80&w=2074&auto=format&fit=crop)'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#2B0A12]/90 via-[#3D1C24]/85 to-[#2B0A12]/90 flex items-center justify-center p-6">
              <div className="text-center">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Get Started Today
                </h3>
                <p className="text-base text-gray-200">
                  Fill out the form and our expert team will contact you within 24 hours
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="relative md:w-3/5 p-4 md:p-6 flex flex-col">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
              aria-label="Close modal"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>

            <div className="mb-3">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-[#2B0A12] mb-1">
                Let's Connect
              </h2>
              <p className="text-xs text-gray-600">
                Share your details and we'll help protect your intellectual property
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-2.5 flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full h-9"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  onBlur={handleEmailBlur}
                  className={`w-full h-9 ${errors.email ? 'border-red-500 border-2' : ''}`}
                  required
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-0.5 font-medium flex items-start">
                    <span className="mr-1">⚠️</span>
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Contact Number *
                </label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-24 px-2 py-1.5 border border-gray-300 rounded-md bg-white text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FFB400] focus:border-[#FFB400] cursor-pointer h-9"
                  >
                    {countryCodes.map((country, index) => (
                      <option key={index} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="tel"
                    placeholder="123-456-7890"
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d\s\-]/g, '');
                      const digitsOnly = value.replace(/[^\d]/g, '');
                      if (digitsOnly.length <= 10) {
                        setFormData({ ...formData, phone: value });
                      }
                    }}
                    onBlur={handlePhoneBlur}
                    className={`flex-1 h-9 ${errors.phone ? 'border-red-500 border-2' : ''}`}
                    required
                    maxLength={12}
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-0.5 font-medium flex items-start">
                    <span className="mr-1">⚠️</span>
                    {errors.phone}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <Textarea
                  placeholder="Tell us about your needs..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={2}
                  className="w-full resize-none text-sm"
                />
              </div>

              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="agree-terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 text-[#D72638] border-gray-300 rounded focus:ring-[#FFB400] cursor-pointer flex-shrink-0"
                  required
                />
                <label htmlFor="agree-terms" className="text-[10px] text-gray-600 cursor-pointer leading-tight">
                  By submitting, you agree to receive communications from Trademark Essential regarding your inquiry.
                </label>
              </div>

              <Button
                type="submit"
                disabled={isLoading || !agreedToTerms}
                className="w-full bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-bold text-sm py-2.5 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Submitting...' : 'Submit Inquiry'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InquiryModal;
